import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { SunIcon, MoonIcon, DocumentTextIcon, PrinterIcon, CalendarDaysIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

/**
 * Libreta de service digital — página PÚBLICA del vehículo (/v/:token).
 * El cliente la abre desde el QR del parabrisas o el link de WhatsApp:
 * su auto, qué se le hizo y qué le toca. Sin precios, sin login.
 *
 * Tema: el MISMO mecanismo del sistema (clase `dark` en documentElement +
 * tokens shadcn bg-background/bg-card/etc de index.css). Switch arriba a la
 * izquierda; oscuro usa el logo blanco, claro el logo negro. Default oscuro.
 */

interface Punto {
  label: string;
  seccion: string | null;
  situacion: 'vencido' | 'pendiente' | 'por_vencer' | 'ok' | 'sin_revisar';
  motivo: string | null;
  doneAt: string | null;
  doneKm: number | null;
  dueAt: string | null;
  dueKm: number | null;
  diasRestantes: number | null;
  kmRestantes: number | null;
}
interface TarjetaService {
  motor: string | null;
  filtros: { aceite: boolean; aire: boolean; combustible: boolean; habitaculo: boolean };
  fluidos: { aceite: boolean; hidraulico: boolean; refrigerante: boolean; liqFreno: boolean };
}
interface Historial { fecha: string; km: number | null; tarjeta: TarjetaService | null; tipos: string[]; trabajos: { descripcion: string; cantidad: number; incluye?: string[] }[] }
interface FechaKm { fecha: string; km: number | null }
interface Cubiertas {
  colocadas: { items: { descripcion: string; cantidad: number }[]; fecha: string; km: number | null } | null;
  alineacion: FechaKm | null;
  balanceo: FechaKm | null;
  rotacion: FechaKm | null;
}
interface Libreta {
  vehiculo: { make: string; model: string; year: number | null; licensePlate: string; km: number | null; kmFecha: string | null; clientNombre: string | null };
  puntos: Punto[];
  historial: Historial[];
  cubiertas: Cubiertas | null;
  proximoCambio: { fecha: string | null; km: number | null } | null;
  turno: { fecha: string; hora: string | null; servicio: string | null } | null;
  presupuesto: { numero: string; fecha: string; validoHasta: string | null; total: number; titulo: string | null } | null;
  diagnostico: { fecha: string } | null;
  taller: { name: string; address?: string; phone?: string; whatsapp?: string; instagram?: string; weekdayHours?: string; saturdayHours?: string };
}

const fmtFecha = (ymd: string | null) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};
const fmtKm = (km: number | null) => (km != null ? `${Number(km).toLocaleString('es-AR')} km` : '');
const fmtMoney = (n: number) => `$ ${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const diaSemana = (ymd: string) => DIAS_SEMANA[new Date(ymd + 'T12:00:00').getDay()];

// Badges por tipo de trabajo (mismos tipos que clasifica el server)
const TIPO_BADGE: Record<string, { label: string; cls: string }> = {
  service: { label: 'Service', cls: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  cubiertas: { label: 'Cubiertas', cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-400' },
  alineacion: { label: 'Alineación', cls: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  frenos: { label: 'Frenos', cls: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  suspension: { label: 'Suspensión', cls: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  bateria: { label: 'Batería', cls: 'bg-teal-500/10 text-teal-600 dark:text-teal-400' }
};
// service primero, después el orden del catálogo
const ORDEN_TIPOS = ['service', 'cubiertas', 'alineacion', 'frenos', 'suspension', 'bateria'];
const tiposDeVisita = (h: Historial) => {
  const set = new Set<string>(h.tipos || []);
  if (h.tarjeta) set.add('service');
  return ORDEN_TIPOS.filter((t) => set.has(t));
};
const BadgesTipos = ({ tipos }: { tipos: string[] }) => (
  <>
    {tipos.map((t) => {
      const b = TIPO_BADGE[t];
      if (!b) return null;
      return (
        <span key={t} className={`inline-block rounded-full px-2 py-px text-[10px] font-bold uppercase tracking-wide ${b.cls}`}>
          {b.label}
        </span>
      );
    })}
  </>
);

// Divisor con título: arma las secciones de la página (pedido de Rodrigo:
// "está toda la info volcada ahí" → cada bloque bajo un título claro)
const SeccionTitulo = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-3 mt-8 flex items-center gap-3 first:mt-1">
    <h2 className="shrink-0 text-[12px] font-black uppercase tracking-[0.25em]">{children}</h2>
    <div className="h-px flex-1 bg-border" />
  </div>
);

// Dentro de cada sección del checklist: primero lo que reclama, después lo ok,
// al final lo gris sin revisar
const ORDEN_SITUACION: Record<string, number> = { vencido: 0, pendiente: 1, por_vencer: 2, ok: 3, sin_revisar: 4 };

// Las secciones del checklist, en el orden del taller (gomería primero)
const ORDEN_SECCIONES = ['Cubiertas', 'Frenos', 'Suspensión y dirección', 'Fluidos y motor', 'Luces y seguridad'];
const porSeccion = (lista: Punto[]) => {
  const grupos = lista.reduce<Record<string, Punto[]>>((acc, p) => {
    const s = p.seccion || 'Otros';
    (acc[s] = acc[s] || []).push(p);
    return acc;
  }, {});
  return Object.entries(grupos).sort(([a], [b]) => {
    const ia = ORDEN_SECCIONES.indexOf(a); const ib = ORDEN_SECCIONES.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
};

// "ONIX PLUS 1.2 LT MT TECH ONSTAR" -> "Onix Plus 1.2 LT MT Tech Onstar":
// las siglas cortas y todo lo que tenga numeros quedan en mayusculas
const modeloLindo = (s: string) =>
  s.trim().split(/\s+/).map((w) => {
    if (w.length <= 3 || /\d/.test(w)) return w.toUpperCase();
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');

const SEV = {
  vencido: {
    dot: 'bg-red-500',
    chip: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30',
    label: 'Vencido'
  },
  pendiente: {
    dot: 'bg-amber-500',
    chip: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    label: 'Pendiente'
  },
  por_vencer: {
    dot: 'bg-yellow-400',
    chip: 'bg-yellow-400/10 text-yellow-600 dark:text-yellow-300 border-yellow-400/30',
    label: 'Se acerca'
  }
} as Record<string, { dot: string; chip: string; label: string }>;

/** Réplica de la tarjeta de service colgante de ROCAM (filtros/fluidos/próx. cambio) */
function TarjetaServiceCard({ t, km, fecha, proximo, taller }: { t: TarjetaService; km: number | null; fecha?: string; proximo: { fecha: string | null; km: number | null } | null; taller?: { phone?: string; instagram?: string } }) {
  const check = (v: boolean) => (
    v
      ? <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">✓</span>
      : <span className="text-sm text-muted-foreground/40">—</span>
  );
  const etiquetaVertical: React.CSSProperties = { writingMode: 'vertical-rl', transform: 'rotate(180deg)' };
  const FILTROS: [string, boolean][] = [
    ['Aceite', t.filtros.aceite],
    ['Aire', t.filtros.aire],
    ['Comb.', t.filtros.combustible],
    ['Habit.', t.filtros.habitaculo]
  ];
  const FLUIDOS: [string, boolean][] = [
    ['Aceite', t.fluidos.aceite],
    ['Hidráulico', t.fluidos.hidraulico],
    ['Refrigerante', t.fluidos.refrigerante],
    ['Líq. freno', t.fluidos.liqFreno]
  ];
  return (
    <div className="mt-2 overflow-hidden rounded-xl border">
      {/* Cabecera con el logo y contacto, como la tarjeta colgante fisica.
          Los dos logos con dark:hidden / dark:block: al imprimir (se fuerza
          claro) aparece solo el negro */}
      <div className="flex flex-col items-center gap-1 border-b px-3 py-2.5">
        <img src="/images/rocam-logo.png" alt="ROCAM" className="h-5 w-auto dark:hidden" />
        <img src="/images/rocam-logo-inverso.png" alt="ROCAM" className="hidden h-5 w-auto dark:block" />
        {(taller?.phone || taller?.instagram) && (
          <div className="text-[10px] text-muted-foreground">
            {[taller?.phone, taller?.instagram].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      {/* FECHA, KM y MOTOR en una fila, etiqueta chiquita adentro de cada celda */}
      <div className="grid grid-cols-[auto_auto_1fr]">
        <div className="px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Fecha</div>
          <div className="text-[13px] font-bold leading-6">{fecha ? fmtFecha(fecha) : '—'}</div>
        </div>
        <div className="border-l px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">KM</div>
          <div className="font-mono text-[13px] font-bold leading-6">{km != null ? Number(km).toLocaleString('es-AR') : '—'}</div>
        </div>
        <div className="border-l px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Motor</div>
          <div className="text-[13px] font-semibold leading-snug">{t.motor || '—'}</div>
        </div>
      </div>
      {/* FILTRO con la etiqueta vertical al costado, como la tarjeta fisica */}
      <div className="flex border-t">
        <div className="flex items-center justify-center bg-zinc-800 px-1.5 text-[9px] font-black uppercase tracking-[0.25em] text-zinc-200" style={etiquetaVertical}>
          Filtro
        </div>
        <div className="grid flex-1 grid-cols-4">
          {FILTROS.map(([label, v], i) => (
            <div key={label} className={`flex flex-col items-center justify-center py-2 ${i > 0 ? 'border-l' : ''}`}>
              <div className="text-[10px] text-muted-foreground">{label}</div>
              {check(v)}
            </div>
          ))}
        </div>
      </div>
      {/* FLUIDO idem */}
      <div className="flex border-t">
        <div className="flex items-center justify-center bg-zinc-800 px-1.5 text-[9px] font-black uppercase tracking-[0.25em] text-zinc-200" style={etiquetaVertical}>
          Fluido
        </div>
        <div className="grid flex-1 grid-cols-2">
          {FLUIDOS.map(([label, v], i) => (
            <div key={label} className={`flex items-center justify-between px-3 py-1.5 ${i % 2 === 1 ? 'border-l' : ''} ${i >= 2 ? 'border-t' : ''}`}>
              <span className="text-xs text-muted-foreground">{label}</span>
              {check(v)}
            </div>
          ))}
        </div>
      </div>
      {/* Próximo cambio (banda roja, solo en el service más reciente) */}
      {proximo && (
        <div className="flex items-center border-t bg-[#dc2626]">
          <div className="px-3 py-2 text-[10px] font-black uppercase leading-tight tracking-wide text-white">
            Próximo<br />cambio
          </div>
          <div className="flex flex-1 items-center justify-end gap-2 py-2 pr-3">
            <span className="rounded-md bg-white/95 px-3 py-1 text-xs font-extrabold text-zinc-900">
              {proximo.fecha ? fmtFecha(proximo.fecha) : '—'}
            </span>
            <span className="rounded-md bg-white/95 px-3 py-1 font-mono text-xs font-extrabold text-zinc-900">
              {proximo.km != null ? `${Number(proximo.km).toLocaleString('es-AR')} km` : '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function PatenteChip({ plate }: { plate: string }) {
  if (!plate) return null;
  // SVG con las proporciones REALES de la chapa argentina (400x130 mm) y los
  // caracteres agrupados como en la calle: "AB 123 CD" / "ABC 123".
  const esVieja = /^[A-Z]{3}\d{3}$/.test(plate) || /^\d{3}[A-Z]{3}$/.test(plate);
  const fuente = "'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif";
  const agrupada = plate
    .replace(/^([A-Z]{2})(\d{3})([A-Z]{2})$/, '$1 $2 $3')
    .replace(/^([A-Z])(\d{3})([A-Z]{3})$/, '$1 $2 $3')
    .replace(/^([A-Z]{3})(\d{3})$/, '$1 $2')
    .replace(/^(\d{3})([A-Z]{3})$/, '$1 $2');

  if (esVieja) {
    return (
      <svg width={200} height={65} viewBox="0 0 400 130" role="img" aria-label={`Patente ${plate}`} style={{ display: 'block' }}>
        <rect x="2" y="2" width="396" height="126" rx="14" fill="#eceef0" stroke="#c3c7cc" strokeWidth="2" />
        <text x="200" y="22" textAnchor="middle" fill="#5da9dc" fontSize="19" fontWeight="700" letterSpacing="4" fontFamily={fuente}>
          ARGENTINA
        </text>
        <g>
          <ellipse cx="26" cy="16" rx="8" ry="10" fill="#dceaf5" stroke="#9fb8ce" strokeWidth="1.2" />
          <circle cx="26" cy="16" r="2.2" fill="#f0c419" />
        </g>
        <g>
          <ellipse cx="374" cy="16" rx="8" ry="10" fill="#dceaf5" stroke="#9fb8ce" strokeWidth="1.2" />
          <circle cx="374" cy="16" r="2.2" fill="#f0c419" />
        </g>
        <rect x="10" y="30" width="380" height="88" rx="9" fill="#0a0a0b" stroke="#26272b" strokeWidth="1.5" />
        {/* textLength estira los caracteres hasta casi los bordes de la placa
            negra: a las chapas reales no les sobra aire a los costados */}
        <text x="200" y="106" textAnchor="middle" fill="#f4f4f3" fontSize="82" fontFamily="'ChapaVieja', 'Arial Narrow', sans-serif" textLength="344" lengthAdjust="spacingAndGlyphs">
          {agrupada}
        </text>
        <rect x="44" y="120" width="22" height="5" rx="2.5" fill="#cfd3d8" />
        <rect x="334" y="120" width="22" height="5" rx="2.5" fill="#cfd3d8" />
      </svg>
    );
  }

  return (
    <svg width={200} height={65} viewBox="0 0 400 130" role="img" aria-label={`Patente ${plate}`} style={{ display: 'block' }}>
      <rect x="2" y="2" width="396" height="126" rx="13" fill="#f7f9fb" stroke="#2e3138" strokeWidth="4" />
      <path d="M5 17 a11 11 0 0 1 11-11 h368 a11 11 0 0 1 11 11 v23 h-390 z" fill="#0b3d91" />
      <g fill="#ffffff">
        <circle cx="22" cy="15" r="1.7" />
        <circle cx="29" cy="12.5" r="1.7" />
        <circle cx="36" cy="11.8" r="1.7" />
        <circle cx="43" cy="12.5" r="1.7" />
        <circle cx="50" cy="15" r="1.7" />
      </g>
      <text x="36" y="31" textAnchor="middle" fill="#ffffff" fontSize="9" fontWeight="700" letterSpacing="1" fontFamily={fuente}>
        MERCOSUR
      </text>
      <text x="200" y="32" textAnchor="middle" fill="#ffffff" fontSize="21" fontWeight="800" letterSpacing="2" fontFamily={fuente}>
        REPUBLICA ARGENTINA
      </text>
      <g>
        <rect x="352" y="11" width="34" height="21" rx="2.5" fill="#ffffff" stroke="#e5e7eb" strokeWidth="0.8" />
        <rect x="352" y="11" width="34" height="7" fill="#75aadb" />
        <rect x="352" y="25" width="34" height="7" fill="#75aadb" />
        <circle cx="369" cy="21.5" r="2.8" fill="#f6b40e" />
      </g>
      <text x="200" y="112" textAnchor="middle" fill="#1b1d21" fontSize="78" letterSpacing="1" fontFamily="'ChapaMercosur', 'Arial Narrow', sans-serif">
        {agrupada}
      </text>
    </svg>
  );
}

export default function PublicVehicleBook() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<Libreta | null>(null);
  const [error, setError] = useState('');
  const [filtroHistorial, setFiltroHistorial] = useState<string | null>(null);
  // Tema con el MISMO mecanismo del sistema: clase `dark` en documentElement.
  // Default oscuro; la eleccion queda guardada en el telefono del cliente.
  const [oscuro, setOscuro] = useState<boolean>(() => {
    try { return localStorage.getItem('rocam-libreta-tema') !== 'claro'; } catch { return true; }
  });

  useEffect(() => {
    // setTimeout(0): el ThemeProvider del sistema (efecto del padre) corre
    // DESPUES de este en el montaje y pisaba la clase; diferido ganamos
    const id = setTimeout(() => document.documentElement.classList.toggle('dark', oscuro), 0);
    return () => clearTimeout(id);
  }, [oscuro]);

  useEffect(() => {
    axios.get(`/api/vehicle-book/public/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'No se pudo cargar la libreta'));
  }, [token]);

  // Imprimir SOLO la etiqueta del service: se fuerza el tema claro (papel)
  // y se restaura al terminar
  const imprimirTarjeta = () => {
    const el = document.documentElement;
    const teniaDark = el.classList.contains('dark');
    if (teniaDark) el.classList.remove('dark');
    const despues = () => {
      if (teniaDark) el.classList.add('dark');
      window.removeEventListener('afterprint', despues);
    };
    window.addEventListener('afterprint', despues);
    window.print();
  };

  const cambiarTema = () => {
    const nuevo = !oscuro;
    setOscuro(nuevo);
    try { localStorage.setItem('rocam-libreta-tema', nuevo ? 'oscuro' : 'claro'); } catch { /* privado */ }
  };

  // En oscuro va el INVERSO del logo (rombo blanco, letras oscuras)
  const logo = oscuro ? '/images/rocam-logo-inverso.png' : '/images/rocam-logo.png';

  const botonTema = (
    <button
      type="button"
      onClick={cambiarTema}
      aria-label="Cambiar tema"
      className="flex h-9 w-9 items-center justify-center rounded-full border bg-card text-muted-foreground transition-colors hover:text-foreground"
    >
      {oscuro ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
    </button>
  );

  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
        <div className="text-center">
          <img src={logo} alt="ROCAM" className="mx-auto h-6 w-auto" />
          <p className="mt-3 text-sm text-muted-foreground">{error || 'Cargando…'}</p>
        </div>
      </div>
    );
  }

  const { vehiculo, puntos, historial, cubiertas, proximoCambio, turno, presupuesto, diagnostico, taller } = data;
  const ultimoService = historial.find((h) => h.tarjeta) || null;
  const ultimaVisita = historial[0] || null;
  // Hero adaptativo: si la última visita fue un service, manda la tarjeta;
  // si fue otro trabajo (cubiertas, frenos...), manda ese trabajo y la
  // tarjeta del último service baja con su fecha
  const heroEsService = !!(ultimaVisita && ultimaVisita.tarjeta);

  // Filtros del historial: solo los tipos que existen en este auto
  const tiposPresentes = ORDEN_TIPOS.filter((t) => historial.some((h) => tiposDeVisita(h).includes(t)));
  const historialVisible = filtroHistorial
    ? historial.filter((h) => tiposDeVisita(h).includes(filtroHistorial))
    : historial;

  // Referencia para sugerir rotación: la última vez que se tocaron posiciones
  // (colocación o rotación), contra el km actual conocido
  const kmRef = (() => {
    if (!cubiertas) return null;
    const cands = [cubiertas.colocadas, cubiertas.rotacion].filter(Boolean) as FechaKm[];
    if (!cands.length) return null;
    cands.sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
    return cands[0].km;
  })();
  const kmDesdeCubiertas = kmRef != null && vehiculo.km != null && vehiculo.km > kmRef ? vehiculo.km - kmRef : null;

  const resumenTarjeta = (t: TarjetaService) => {
    const filtros = [
      t.filtros.aceite && 'aceite',
      t.filtros.aire && 'aire',
      t.filtros.combustible && 'combustible',
      t.filtros.habitaculo && 'habitáculo'
    ].filter(Boolean).join(', ');
    const fluidos = [
      t.fluidos.hidraulico && 'hidráulico',
      t.fluidos.refrigerante && 'refrigerante',
      t.fluidos.liqFreno && 'líq. de freno'
    ].filter(Boolean).join(', ');
    return { filtros, fluidos };
  };
  const atencion = puntos.filter((p) => p.situacion !== 'ok' && p.situacion !== 'sin_revisar');
  const waLink = taller.whatsapp
    ? `https://wa.me/${taller.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola! Quiero un turno para el ${vehiculo.make || ''} ${vehiculo.model || ''} (${vehiculo.licensePlate || ''})`)}`
    : null;

  return (
    <div className="min-h-screen bg-background pb-14 text-foreground antialiased">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="mx-auto max-w-xl px-5 pb-7 pt-5 md:max-w-4xl">
          {/* logo a la izquierda, switch de tema a la derecha */}
          <div className="flex items-center justify-between">
            <img src={logo} alt="ROCAM" className="h-6 w-auto" />
            {botonTema}
          </div>
          <div className="md:flex md:items-center md:justify-between md:gap-8">
          <div>
          <div className="mt-4 text-[11px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
            Libreta de mantenimiento
          </div>

          {vehiculo.clientNombre && (
            <div className="mt-3 text-sm font-semibold text-muted-foreground">{vehiculo.clientNombre}</div>
          )}
          <h1 className="mt-1 text-xl font-bold leading-snug tracking-tight">
            {modeloLindo(`${vehiculo.make || ''} ${vehiculo.model || ''}`)}{vehiculo.year ? ` ${vehiculo.year}` : ''}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <PatenteChip plate={vehiculo.licensePlate} />
          </div>
          {ultimaVisita && (
            <div className="mt-3 text-sm text-muted-foreground">
              Última visita: <span className="font-semibold text-foreground">{fmtFecha(ultimaVisita.fecha)}</span>
              {vehiculo.km != null && (
                <>
                  {' · '}
                  <span className="font-mono font-semibold text-foreground">{fmtKm(vehiculo.km)}</span>
                </>
              )}
            </div>
          )}
          </div>

          {/* En PC el boton de turno vive en el header, aprovechando el hueco */}
          {waLink && (
            <a
              href={waLink}
              className="hidden shrink-0 items-center gap-2.5 rounded-2xl bg-[#25D366] px-6 py-4 text-[15px] font-extrabold text-[#073b1d] shadow-lg transition-transform active:scale-[0.98] md:flex"
              style={{ textDecoration: 'none' }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.83 14.12c-.25.7-1.45 1.33-2.02 1.42-.52.08-1.17.11-1.88-.12-.44-.14-1-.32-1.71-.63-3.02-1.3-4.99-4.34-5.14-4.54-.15-.2-1.23-1.63-1.23-3.11 0-1.48.78-2.21 1.05-2.51.28-.3.6-.38.8-.38h.58c.18 0 .43-.07.68.52.25.6.85 2.07.92 2.22.08.15.13.33.03.53-.5 1-1.04.96-.77 1.42.99 1.71 1.99 2.3 3.5 3.06.26.13.41.11.56-.06.15-.18.64-.75.81-1 .17-.25.35-.21.58-.13.24.09 1.52.72 1.78.85.26.13.44.19.5.3.07.11.07.65-.18 1.34z"/>
              </svg>
              Pedir turno
            </a>
          )}
          </div>
        </div>
      </div>

      {/* En PC: dos columnas (servicio y acciones | checklist e historial);
          en el celular todo apilado como siempre */}
      <div className="mx-auto mt-4 max-w-xl px-5 md:max-w-4xl">
        {/* Sección PENDIENTES: lo que el cliente tiene abierto con el taller */}
        {(turno || presupuesto) && (
        <>
        <SeccionTitulo>Pendientes</SeccionTitulo>
        <div className="space-y-4 md:grid md:grid-cols-2 md:items-start md:gap-4 md:space-y-0">
        {turno && (
          <section className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <CalendarDaysIcon className="mt-0.5 h-6 w-6 shrink-0 text-sky-600 dark:text-sky-400" />
              <div className="min-w-0">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Turno programado</h2>
                <div className="mt-1 text-[15px] font-bold">
                  Te esperamos el {diaSemana(turno.fecha)} {fmtFecha(turno.fecha)}{turno.hora ? ` a las ${turno.hora}` : ''}
                </div>
                {turno.servicio && <div className="mt-0.5 text-sm text-muted-foreground">{turno.servicio}</div>}
              </div>
            </div>
          </section>
        )}

        {/* Presupuesto esperando respuesta */}
        {presupuesto && (
          <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <ClipboardDocumentListIcon className="mt-0.5 h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 flex-1">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Presupuesto pendiente</h2>
                <div className="mt-1 text-[15px] font-bold">
                  {presupuesto.titulo || `Presupuesto ${presupuesto.numero}`}
                </div>
                <div className="mt-0.5 text-sm text-muted-foreground">
                  {fmtMoney(presupuesto.total)}
                  {presupuesto.validoHasta ? ` · válido hasta el ${fmtFecha(presupuesto.validoHasta)}` : ''}
                </div>
                {taller.whatsapp && (
                  <a
                    href={`https://wa.me/${taller.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola! Quería coordinar por el presupuesto ${presupuesto.numero} del ${modeloLindo(`${vehiculo.make || ''} ${vehiculo.model || ''}`)} (${vehiculo.licensePlate || ''})`)}`}
                    className="mt-2.5 inline-block rounded-xl bg-foreground px-4 py-2 text-[13px] font-bold text-background transition-transform active:scale-[0.98]"
                    style={{ textDecoration: 'none' }}
                  >
                    Coordinar por WhatsApp
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        </div>
        </>
        )}

        {/* Sección ÚLTIMA VISITA: el trabajo más reciente, con su formato */}
        {ultimaVisita && <SeccionTitulo>Última visita</SeccionTitulo>}
        {!heroEsService && ultimaVisita && (
          <section className="rounded-2xl border bg-card p-5 shadow-sm md:max-w-2xl">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                <span className="text-[15px] font-bold text-foreground">{fmtFecha(ultimaVisita.fecha)}</span>
                {ultimaVisita.km != null ? <> · <span className="font-mono font-semibold">{fmtKm(ultimaVisita.km)}</span></> : null}
              </div>
              <div className="flex flex-wrap gap-1"><BadgesTipos tipos={tiposDeVisita(ultimaVisita)} /></div>
            </div>
            <ul className="mt-2 space-y-1">
              {ultimaVisita.trabajos.map((t, j) => (
                <li key={j} className="flex items-start gap-2 text-[14px] leading-snug">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                  <span>
                    {t.descripcion}{t.cantidad > 1 ? <span className="text-muted-foreground"> ×{t.cantidad}</span> : ''}
                    {(t.incluye || []).length > 0 && (
                      <span className="mt-0.5 block space-y-px text-[12.5px] text-muted-foreground">
                        {(t.incluye || []).map((inc, k) => <span key={k} className="block">· {inc}</span>)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* La tarjeta del último service: dentro de "Última visita" si fue lo
            último; si no, sección propia (la fecha va en la celda FECHA) */}
        {!heroEsService && ultimoService && ultimoService.tarjeta && <SeccionTitulo>Último service</SeccionTitulo>}
        {ultimoService && ultimoService.tarjeta && (
          <section id="tarjeta-service" className="rounded-2xl border bg-card p-5 shadow-sm md:max-w-md">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Tarjeta de service</h2>
              <button
                type="button"
                onClick={imprimirTarjeta}
                aria-label="Imprimir etiqueta"
                className="no-print flex h-7 w-7 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:text-foreground"
              >
                <PrinterIcon className="h-4 w-4" />
              </button>
            </div>
            <TarjetaServiceCard t={ultimoService.tarjeta} km={ultimoService.km ?? vehiculo.km} fecha={ultimoService.fecha} proximo={proximoCambio} taller={taller} />
            {/* Al imprimir: solo esta etiqueta, en claro, tamano tarjeta */}
            <style>{`@media print {
              body * { visibility: hidden !important; }
              #tarjeta-service, #tarjeta-service * { visibility: visible !important; }
              #tarjeta-service { position: absolute; left: 0; top: 0; width: 100mm; border: none !important; box-shadow: none !important; }
              #tarjeta-service .no-print { display: none !important; }
            }`}</style>
          </section>
        )}

        {/* Sección ESTADO DEL VEHÍCULO: cubiertas + checklist por sistema */}
        <SeccionTitulo>Estado del vehículo</SeccionTitulo>
        {/* Con cubiertas: dos columnas en PC; sin cubiertas el checklist va entero */}
        <div className={`space-y-4 ${cubiertas ? 'md:grid md:grid-cols-2 md:items-start md:gap-4 md:space-y-0' : ''}`}>
        <div className="space-y-4">
        {/* Cubiertas: qué tiene puesto el auto y cuándo se trabajó */}
        {cubiertas && (
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Cubiertas</h2>
            {cubiertas.colocadas && (
              <div className="mt-2">
                {cubiertas.colocadas.items.map((t, j) => (
                  <div key={j} className="text-[15px] font-bold leading-snug">
                    {t.descripcion}{t.cantidad > 1 ? <span className="font-semibold text-muted-foreground"> ×{t.cantidad}</span> : ''}
                  </div>
                ))}
                <div className="mt-1 text-sm text-muted-foreground">
                  Colocadas el {fmtFecha(cubiertas.colocadas.fecha)}
                  {cubiertas.colocadas.km != null ? <> · <span className="font-mono">{fmtKm(cubiertas.colocadas.km)}</span></> : null}
                  {kmDesdeCubiertas != null && cubiertas.rotacion == null ? ` · llevan ~${kmDesdeCubiertas.toLocaleString('es-AR')} km` : ''}
                </div>
              </div>
            )}
            {(cubiertas.alineacion || cubiertas.balanceo || cubiertas.rotacion) && (
              <div className={`space-y-1 text-sm text-muted-foreground ${cubiertas.colocadas ? 'mt-3 border-t pt-3' : 'mt-2'}`}>
                {cubiertas.alineacion && (
                  <div>Última alineación: <span className="font-semibold text-foreground">{fmtFecha(cubiertas.alineacion.fecha)}</span>{cubiertas.alineacion.km != null ? ` · ${fmtKm(cubiertas.alineacion.km)}` : ''}</div>
                )}
                {cubiertas.balanceo && cubiertas.balanceo.fecha !== cubiertas.alineacion?.fecha && (
                  <div>Último balanceo: <span className="font-semibold text-foreground">{fmtFecha(cubiertas.balanceo.fecha)}</span>{cubiertas.balanceo.km != null ? ` · ${fmtKm(cubiertas.balanceo.km)}` : ''}</div>
                )}
                {cubiertas.rotacion && (
                  <div>Última rotación: <span className="font-semibold text-foreground">{fmtFecha(cubiertas.rotacion.fecha)}</span>{cubiertas.rotacion.km != null ? ` · ${fmtKm(cubiertas.rotacion.km)}` : ''}</div>
                )}
              </div>
            )}
            <div className={`mt-3 rounded-xl px-3.5 py-2.5 text-[13px] leading-snug ${kmDesdeCubiertas != null && kmDesdeCubiertas > 10000
              ? 'border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'bg-muted/50 text-muted-foreground'}`}>
              {kmDesdeCubiertas != null && kmDesdeCubiertas > 10000
                ? 'Ya conviene rotarlas: se recomienda rotar las cubiertas cada 10.000 km para que gasten parejo.'
                : 'Se recomienda rotar las cubiertas cada 10.000 km y alinear ante desgaste desparejo o tirones del volante.'}
            </div>
          </section>
        )}

        </div>

        {/* Columna del checklist en PC */}
        <div className="space-y-4">
        {/* El checklist ENTERO del taller: verde al día, punto de color si
            reclama, gris lo que todavía no se revisó (así no se ve recortado) */}
        {puntos.length > 0 && (
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Checklist del vehículo</h2>
            {atencion.length === 0 && (
              <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-xs font-black text-white">✓</span>
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">Todo al día. Nada pendiente por ahora.</span>
              </div>
            )}
            <div className="mt-3 space-y-3">
              {porSeccion(puntos).map(([seccion, items]) => (
                <div key={seccion}>
                  <div className="text-[11px] font-semibold text-muted-foreground">{seccion}</div>
                  <div className="mt-1 grid grid-cols-1 gap-y-1 sm:grid-cols-2 sm:gap-x-4">
                    {items.slice().sort((a, b) => (ORDEN_SITUACION[a.situacion] ?? 9) - (ORDEN_SITUACION[b.situacion] ?? 9)).map((p, i) => {
                      // Lo que reclama va destacado ACA, en su seccion, con su
                      // detalle: una sola lista, sin card aparte (era redundante)
                      if (p.situacion !== 'ok' && p.situacion !== 'sin_revisar') {
                        const sev = SEV[p.situacion] || SEV.pendiente;
                        const vence = [
                          p.dueAt ? `vence ${fmtFecha(p.dueAt)}` : null,
                          p.dueKm != null ? `a los ${fmtKm(p.dueKm)}` : null
                        ].filter(Boolean).join(' · ');
                        return (
                          <div key={i} className="flex items-start gap-2 rounded-lg border bg-muted/40 px-2.5 py-2 sm:col-span-2">
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                              <span className={`h-2.5 w-2.5 rounded-full ${sev.dot}`} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-baseline gap-x-2">
                                <span className="text-[13px] font-bold">{p.label}</span>
                                <span className={`rounded-full border px-2 py-px text-[10px] font-bold uppercase tracking-wide ${sev.chip}`}>{sev.label}</span>
                              </div>
                              <div className="mt-0.5 text-[11px] text-muted-foreground">
                                {p.situacion === 'pendiente' ? (p.motivo || 'Detectado en el último control') : vence}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={i} className="flex items-center gap-2">
                          {p.situacion === 'ok' ? (
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[9px] font-black text-emerald-600 dark:text-emerald-400">✓</span>
                          ) : (
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-sm leading-none text-muted-foreground/50">–</span>
                          )}
                          <span className={`text-[13px] ${p.situacion === 'sin_revisar' ? 'text-muted-foreground/60' : ''}`}>{p.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">– = todavía no se revisó en el taller</div>
          </section>
        )}

        {/* Informe del diagnostico (PDF con token fresco via redirect) */}
        {diagnostico && (
          <a
            href={`/api/vehicle-book/public/${token}/diagnostico-pdf`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-2xl border bg-card py-3.5 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-muted/50"
            style={{ textDecoration: 'none' }}
          >
            <DocumentTextIcon className="h-5 w-5 text-muted-foreground" />
            Ver informe del control ({fmtFecha(diagnostico.fecha)}) — PDF
          </a>
        )}
        </div>
        </div>

        {/* Sección HISTORIAL: todos los trabajos registrados */}
        <SeccionTitulo>Historial completo</SeccionTitulo>
        <section className="rounded-2xl border bg-card p-5 shadow-sm">
          {historial.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay trabajos registrados.</p>
          ) : (
            <div>
              {/* chips para filtrar por tipo de trabajo */}
              {tiposPresentes.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setFiltroHistorial(null)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filtroHistorial == null ? 'border-transparent bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Todos
                  </button>
                  {tiposPresentes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setFiltroHistorial(filtroHistorial === t ? null : t)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filtroHistorial === t ? 'border-transparent bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {TIPO_BADGE[t]?.label || t}
                    </button>
                  ))}
                </div>
              )}
              {/* tabla estilo libreta de papel: Fecha | Km | Trabajos */}
              <div className="grid grid-cols-[78px_62px_1fr] gap-x-3 border-b pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                <div>Fecha</div>
                <div>Km</div>
                <div>Trabajos</div>
              </div>
              {historialVisible.map((h, i) => (
                <div key={i} className={`grid grid-cols-[78px_62px_1fr] gap-x-3 py-3 ${i > 0 ? 'border-t' : ''}`}>
                  <div>
                    <div className="text-[13px] font-bold leading-tight">{fmtFecha(h.fecha)}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <BadgesTipos tipos={tiposDeVisita(h)} />
                    </div>
                  </div>
                  <div className="font-mono text-[11.5px] leading-5 text-muted-foreground">
                    {h.km != null ? Number(h.km).toLocaleString('es-AR') : '—'}
                  </div>
                  <div className="min-w-0">
                    {h.tarjeta && (() => {
                      const r = resumenTarjeta(h.tarjeta);
                      return (
                        <div className="space-y-0.5 text-[13.5px] leading-snug text-muted-foreground">
                          {h.tarjeta.motor && <div className="font-medium text-foreground">{h.tarjeta.motor}</div>}
                          {r.filtros && <div>Filtros: {r.filtros}</div>}
                          {r.fluidos && <div>Fluidos: {r.fluidos}</div>}
                        </div>
                      );
                    })()}
                    {h.trabajos.length > 0 && (
                      <ul className={`space-y-0.5 ${h.tarjeta ? 'mt-1' : ''}`}>
                        {h.trabajos.map((t, j) => (
                          <li key={j} className="text-[13.5px] leading-snug text-muted-foreground">
                            {t.descripcion}{t.cantidad > 1 ? <span className="opacity-60"> ×{t.cantidad}</span> : ''}
                            {(t.incluye || []).length > 0 && (
                              <span className="mt-0.5 block space-y-px text-[12px] opacity-80">
                                {(t.incluye || []).map((inc, k) => <span key={k} className="block pl-2">· {inc}</span>)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Turno por WhatsApp (en PC vive en el header) */}
        {waLink && (
          <a
            href={waLink}
            className="mt-5 flex items-center justify-center gap-2.5 rounded-2xl bg-[#25D366] px-5 py-4 text-[15px] font-extrabold text-[#073b1d] shadow-lg transition-transform active:scale-[0.98] md:hidden"
            style={{ textDecoration: 'none' }}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
              <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.83 14.12c-.25.7-1.45 1.33-2.02 1.42-.52.08-1.17.11-1.88-.12-.44-.14-1-.32-1.71-.63-3.02-1.3-4.99-4.34-5.14-4.54-.15-.2-1.23-1.63-1.23-3.11 0-1.48.78-2.21 1.05-2.51.28-.3.6-.38.8-.38h.58c.18 0 .43-.07.68.52.25.6.85 2.07.92 2.22.08.15.13.33.03.53-.5 1-1.04.96-.77 1.42.99 1.71 1.99 2.3 3.5 3.06.26.13.41.11.56-.06.15-.18.64-.75.81-1 .17-.25.35-.21.58-.13.24.09 1.52.72 1.78.85.26.13.44.19.5.3.07.11.07.65-.18 1.34z"/>
            </svg>
            Pedir turno por WhatsApp
          </a>
        )}

        {/* Taller */}
        <footer className="pt-4 text-center">
          <img src={logo} alt={taller.name || 'ROCAM'} className="mx-auto h-5 w-auto opacity-80" />
          <div className="mt-2.5 space-y-0.5 text-xs leading-relaxed text-muted-foreground">
            {taller.address && <div>{taller.address}</div>}
            {(taller.weekdayHours || taller.saturdayHours) && (
              <div>
                {taller.weekdayHours ? `Lun a Vie ${taller.weekdayHours}` : ''}
                {taller.weekdayHours && taller.saturdayHours ? ' · ' : ''}
                {taller.saturdayHours ? `Sáb ${taller.saturdayHours}` : ''}
              </div>
            )}
            {taller.phone && <div>Tel {taller.phone}</div>}
            {taller.instagram && <div>{taller.instagram}</div>}
          </div>
        </footer>
      </div>
    </div>
  );
}
